import React, { useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'

import { useSetState } from '@campaignhub/react-hooks'

import {
  Box, Checkbox, DashboardModule, ListItem, Text,
} from '@campaignhub/suit-theme'

import chunkArray from '@functions/chunkArray'
import useServiceJobDetail from '@hooks/useServiceJobDetail'

import MutationStatus from '../MutationStatus'

const defaultState = {
  checkedIds: [],
}

const ProviderChecklist = (props) => {
  const { providerChecklist, hidden, serviceJobChecklistItemTypes } = props

  const [state, setState] = useSetState(defaultState)
  const { checkedIds } = state

  const {
    callbacks: {
      updateServiceJobDetail: updateFn,
    },
    updating,
  } = useServiceJobDetail()

  const itemList = useMemo(
    () => (providerChecklist || []).map(x => ({ ...JSON.parse(x.value), id: x.id })),
    [providerChecklist],
  )

  const chunkedList = useMemo(() => chunkArray(itemList, 10, 3), [itemList])

  const checkedSet = useMemo(() => new Set(checkedIds), [checkedIds])

  const seeded = useRef(false)

  useEffect(() => {
    if (!seeded.current && itemList.length) {
      setState({ checkedIds: itemList.filter(x => x.checked).map(x => x.id) })
      seeded.current = true
    }
  }, [itemList])

  const updateChecklist = (id) => {
    const selectedChecklist = providerChecklist.find(x => x.id === id)
    const checklistValue = JSON.parse(selectedChecklist.value)
    const isChecked = checkedIds.includes(id)

    checklistValue.checked = !isChecked
    const updatedChecklistIds = isChecked ? checkedIds.filter(x => x !== id) : [...checkedIds, id]

    setState({ checkedIds: updatedChecklistIds })

    updateFn({ id, value: JSON.stringify(checklistValue) }).then(({ success, errors }) => {
      if (!success) {
        toast.warning(errors[0])
        setState({ checkedIds })
      }
    })
  }

  if (!providerChecklist?.length || hidden) return null

  return (
    <DashboardModule
      title="Provider Checklist"
      headerRight={<MutationStatus isMutating={updating} />}
    >
      <Box padding="large" flexDirection={['column', 'row']} lineHeight="1.3" gridGap={[0, 'medium']}>
        {chunkedList.map((chunk, chunkIndex) => (
          serviceJobChecklistItemTypes && Object.keys(serviceJobChecklistItemTypes).map((typeId) => {
            const items = chunk.filter(item => (
              item.serviceJobCheckListItemTypeId == typeId || (typeId == 0 && !item.serviceJobCheckListItemTypeId)
            ))
            if (!items.length) return null
            return (
              <Box key={`${chunkIndex}-${typeId}`} flexDirection="column">
                {typeId != 0 && (
                  <ListItem boxProps={{ paddingBottom: 'small' }} flexDirection="row" disableHover>
                    <Text fontSize="small">{serviceJobChecklistItemTypes[typeId].description}</Text>
                  </ListItem>
                )}
                {items.map(item => (
                  <ListItem key={item.id} boxProps={{ padding: '6px 0px' }} flexDirection="row" disableHover>
                    <Checkbox checked={checkedSet.has(item.id)} onClick={() => updateChecklist(item.id)} />
                    <Text fontSize="small">{item.description}</Text>
                  </ListItem>
                ))}
              </Box>
            )
          })
        ))}
      </Box>
    </DashboardModule>
  )
}

ProviderChecklist.propTypes = {
  providerChecklist: PropTypes.array,
  hidden: PropTypes.bool,
  serviceJobChecklistItemTypes: PropTypes.array
}

export default ProviderChecklist
